/**
 * ACIE (AdMesh Commercial Intent Engine) API integration
 * Provides intent analysis functionality for the AdMesh UI SDK
 */

import type { AdMeshSDKConfig } from '../sdk/AdMeshSDK';
import type { Suggestion } from '../components/AdMeshIntentAssistant';
import type { PlatformResponse } from '../types/index';

export interface CommercialIntentRequest {
  url: string;
  title?: string;
  headings?: string[];
  article_text?: string;
  domain: string;
  article_slug?: string;
  agent_id?: string;
  cpx_floor?: number;
  source: string;
  api_key: string;
  user_id?: string;
}

export interface CommercialIntentResult {
  url: string;
  domain: string;
  suggestions: Suggestion[];
}

type AcieRecommendationProduct = {
  product_id?: string;
  product_link?: string;
  product_click_url?: string;
  product_name?: string;
  product_description?: string;
  product_price?: string | number | null;
  product_discount?: string | number | null;
  product_currency?: string;
  product_discount_type?: string;
  product_cta_label?: string;
  product_image_url?: string;
  product_features?: string[];
};

type AcieRecommendationPayload = {
  recommendation_id?: string;
  title?: string;
  url?: string;
  click_url?: string;
  exposure_url?: string;
  follow_up_suggestion?: string;
  followup_engagement_url?: string;
  followup_exposure_url?: string;
  preferred_format?: string;
  products?: AcieRecommendationProduct[];
  creative_input?: {
    brand_name?: string;
    product_name?: string;
    short_description?: string;
    long_description?: string;
    cta_label?: string;
    cta_url?: string;
    assets?: {
      image_urls?: string[];
      logo_url?: string;
    };
  };
};

function isPlatformResponse(value: unknown): value is PlatformResponse {
  return Boolean(
    value &&
    typeof value === 'object' &&
    'recommendation_id' in value &&
    'status' in value
  );
}

function normalizeAcieRecommendation(value: unknown): PlatformResponse | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  if (isPlatformResponse(value)) {
    return value;
  }

  const recommendation = value as AcieRecommendationPayload;
  if (!recommendation.recommendation_id) {
    return undefined;
  }

  const products = (recommendation.products || []).map((product) => ({
    product_id: product.product_id || '',
    product_link: product.product_link || product.product_click_url || recommendation.click_url || recommendation.url || '',
    product_name: product.product_name || recommendation.title || recommendation.creative_input?.product_name || '',
    product_description: product.product_description || '',
    product_price: product.product_price ?? undefined,
    product_discount: product.product_discount ?? undefined,
    product_currency: product.product_currency,
    product_discount_type: product.product_discount_type,
    product_cta_label: product.product_cta_label || recommendation.creative_input?.cta_label,
    product_image_url: product.product_image_url,
    product_features: product.product_features,
  }));

  return {
    spec_version: '1.0',
    recommendation_id: recommendation.recommendation_id,
    timestamp: new Date().toISOString(),
    status: 'generated',
    ttl_ms: 300000,
    recommendation: {
      format: (recommendation.preferred_format as NonNullable<PlatformResponse['recommendation']>['format']) || 'tail',
      disclosure: 'Sponsored',
      creative: {
        brand_name: recommendation.creative_input?.brand_name || '',
        headline: recommendation.title || recommendation.creative_input?.product_name || '',
        description:
          recommendation.creative_input?.short_description ||
          recommendation.creative_input?.long_description ||
          '',
        cta_text: recommendation.creative_input?.cta_label || products[0]?.product_cta_label || 'Learn more',
        follow_up_suggestion: recommendation.follow_up_suggestion,
        logo_url: recommendation.creative_input?.assets?.logo_url,
        image_urls: recommendation.creative_input?.assets?.image_urls || [],
        landing_page_url: recommendation.url || recommendation.click_url || recommendation.creative_input?.cta_url || '',
        products,
      },
    },
    tracking: {
      exposure_url: recommendation.exposure_url,
      click_url: recommendation.click_url,
      followup_exposure_url: recommendation.followup_exposure_url,
      followup_engagement_url: recommendation.followup_engagement_url,
    },
  };
}

function normalizeCommercialIntentResult(result: CommercialIntentResult): CommercialIntentResult {
  return {
    ...result,
    suggestions: (result.suggestions || []).map((suggestion) => ({
      ...suggestion,
      recommendation: normalizeAcieRecommendation(suggestion.recommendation),
    })),
  };
}

/**
 * Extract article slug from URL for publisher API keys
 */
function extractArticleSlug(url: string): string | undefined {
  try {
    const urlObj = new URL(url);
    const pathSegments = urlObj.pathname.split('/').filter(segment => segment.length > 0);
    const articleSlug = pathSegments[pathSegments.length - 1];
    return articleSlug || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Call ACIE analyze-intent endpoint to analyze commercial intent of a page
 * 
 * @param headingsData - Page data including title, headings, URL, and domain
 * @param config - AdMesh SDK configuration containing API base URL and API key
 * @returns Promise<CommercialIntentResult | null> - Intent analysis results or null on error
 */
export async function callAnalyzeIntentEndpoint(
  headingsData: {
    url: string;
    title?: string;
    headings?: string[];
    article_text?: string;
    domain: string;
  },
  config: AdMeshSDKConfig
): Promise<CommercialIntentResult | null> {
  try {
    console.log('[AdMesh SDK] Starting ACIE analyze-intent call...', { url: headingsData.url });
    
    // Combine all headings into content for analysis
    const allHeadings = Array.isArray(headingsData.headings) ? headingsData.headings : [];
    const pathTopic = extractArticleSlug(headingsData.url)
      ?.replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const fallbackHeading = pathTopic
      ? pathTopic.replace(/\b\w/g, (char) => char.toUpperCase())
      : undefined;
    
    // Extract article slug for publisher API keys
    const articleSlug = config.apiKey.toLowerCase().startsWith('pub_') 
      ? extractArticleSlug(headingsData.url)
      : undefined;

    const normalizedSource = config.extension ? 'extension' : 'admesh_ui_sdk';
    const normalizedDomain = (
      headingsData.domain ||
      (typeof window !== 'undefined' ? window.location.hostname : '')
    ).trim().toLowerCase();
    
    // Prepare request payload matching CommercialIntentRequest model
    const payload: CommercialIntentRequest = {
      url: headingsData.url,
      title: headingsData.title || undefined,
      headings: allHeadings.length > 0 ? allHeadings : fallbackHeading ? [fallbackHeading] : undefined,
      article_text: headingsData.article_text?.trim() || undefined,
      domain: normalizedDomain,
      article_slug: articleSlug,
      agent_id: undefined, // Optional - can be added later if needed
      cpx_floor: undefined, // Optional - can be added later if needed
      source: normalizedSource,
      api_key: config.apiKey, // Add API key from config
      user_id: config.userId || undefined
    };
    
    console.log('[AdMesh SDK] Payload prepared:', {
      url: payload.url,
      titleLength: payload.title?.length || 0,
      headingsCount: payload.headings?.length || 0,
      articleTextLength: payload.article_text?.length || 0,
      domain: payload.domain,
      article_slug: payload.article_slug,
      source: payload.source,
      has_user_id: !!payload.user_id
    });
    
    const apiUrl = `${config.apiBaseUrl}/acie/analyze-intent`;
    console.log('[AdMesh SDK] Calling ACIE API:', apiUrl);
    
    // Make API call to ACIE endpoint
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.apiKey}`
      },
      body: JSON.stringify(payload)
    });
    
    console.log('[AdMesh SDK] ACIE API response received:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[AdMesh SDK] ACIE API error:', {
        status: response.status,
        statusText: response.statusText,
        errorText: errorText
      });
      throw new Error(`ACIE API error: ${response.status} - ${errorText}`);
    }
    
    const rawAnalysisResult = await response.json();
    const analysisResult = normalizeCommercialIntentResult(rawAnalysisResult as CommercialIntentResult);
    
    console.log('[AdMesh SDK] ACIE analysis completed:', {
      url: headingsData.url,
      suggestionsCount: analysisResult.suggestions.length
    });
    
    // Return the analysis result
    return analysisResult;
    
  } catch (err) {
    console.error('[AdMesh SDK] Error calling ACIE analyze-intent endpoint:', err);
    return null;
  }
}
